import BasePage from "../../../components/layout/BasePage"
import HeadBanner from "../../../components/elements/BaseHeadBanner"
import { useContext, useMemo, useState } from "react"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { useQuery } from "@tanstack/react-query"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import { getDataFromSessionStorage, useDelayWhenSearchWithPagination, useResetPageOnFilterChange } from "../../../services/functions"
import { MajorOrderGet } from "../../../data/Types"
import { P } from "../../../auth/permissions"
import { Pagination } from "antd"
import { getPaginatedData } from "../../api/apiCalls"
import BaseFilterModal from "../../../components/elements/BaseFilterModal"
import { ReactComponent as PLUSINCIRCLE } from "../../../assets/PlusInCircleIcon.svg"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import BaseTable, { ColumnType } from "../../../components/elements/BaseTable"
import MajorOrderTableRow from "../components/majorOrder/MajorOrderTableRow"
import AddMajorOrderModal from "../components/majorOrder/AddMajorOrderModal"
import SearchbarAndNavigationButtons from "../components/SearchbarAndNavigationButtons"
import { GeneralQueryKeys, LocalStorageKeys } from "../../../api/constants"
import FloatingActionButton from "../components/FloatingActionButton"
import { Authorized } from "../../../components/authentication/Authorized"
export default function MajorOrderPage() {
    // variables for pagination
    const [page, setpage] = useState(getDataFromSessionStorage(LocalStorageKeys.MAJOR_ORDER_FILTER + "page") ?? 1)
    const handlePageChange = (page: number) => {
        sessionStorage.setItem(LocalStorageKeys.MAJOR_ORDER_FILTER + "page", JSON.stringify(page))
        setpage(page)
    }

    // variables for search in pagination
    const [searchTerm, setsearchTerm] = useState(getDataFromSessionStorage(LocalStorageKeys.MAJOR_ORDER_FILTER + "searchTerm"))
    const searchParams = useDelayWhenSearchWithPagination(searchTerm)
    const [filterDateSince, setfilterDateSince] = useState(getDataFromSessionStorage(LocalStorageKeys.MAJOR_ORDER_FILTER + "dateSince"))
    const [filterDateTo, setfilterDateTo] = useState(getDataFromSessionStorage(LocalStorageKeys.MAJOR_ORDER_FILTER + "dateTo"))
    const [invertSort, setinvertSort] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.MAJOR_ORDER_FILTER + "invertSort"))
    const [filterArchived, setfilterArchived] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.MAJOR_ORDER_FILTER + "archived"))

    useResetPageOnFilterChange([searchParams, filterDateSince, filterDateTo, filterArchived, invertSort], () => handlePageChange(1))
    
    // Get Data Query
    const queryKey = GeneralQueryKeys.MAJOR_ORDER_ALL + page + searchParams + filterDateSince + filterDateTo + filterArchived + invertSort
    const { error, data, isLoading } = useQuery({
        queryKey: [queryKey],
        queryFn: () =>
            getPaginatedData({
                pageType: "major-order",
                page: page,
                limit: 15,
                search: searchParams,
                startDate: filterDateSince,
                endDate: filterDateTo,
                archived: filterArchived,
                invertSort: invertSort,
            }),
    })

    const modalCon = useContext(ModalContext)

    const [showFilter, setshowFilter] = useState(false)

    const handleRemoveFilter = () => {
        setfilterDateTo("")
        setfilterDateSince("")
        setfilterArchived(false)
    }

    // FRONTEND LOGIC
    const displayedMajorOrders = useMemo(() => data?.docs, [data?.docs])
    const MajorOrderTableColumns: ColumnType[] = [
        {
            label: "Datum",
            responsive: "table-cell",
            width: "min-w-[10rem] w-fit",
        },
        {
            label: "Kunde",
            responsive: "table-cell",
            width: "min-w-[10rem] w-fit",
        },
        {
            label: "Objekt",
            responsive: "hidden xl:table-cell",
            width: " min-w-[9rem] w-fit",
        },
        {
            label: "Gesamtbetrag (netto)",
            responsive: "table-cell",
            width: " min-w-[9rem] w-fit",
            permissions: [P.buchhaltung_read],
        },
        {
            label: "Abschläge (netto)",
            responsive: "hidden lg:table-cell",
            width: " min-w-[9rem] w-fit",
            permissions: [P.buchhaltung_read],
        },
        {
            label: "Beschreibung",
            width: "sm:min-w-[8rem] w-fit",
            responsive: "hidden xl:table-cell",
        },
        {
            label: "Anzahl Rechnungen",
            width: "sm:min-w-[8rem] w-fit",
            responsive: "hidden md:table-cell",
        },
        {
            label: "Status",
        },
    ]

    return (
        <BasePage>
            <HeadBanner
                title={"Aufträge"}
                button={
                    <Authorized anyOf={[P.buchhaltung_read_write, P.buchhaltung_read_write_restricted]}>
                        <div className="flex gap-4">
                            <PLUSINCIRCLE className="w-8 h-8 cursor-pointer hidden md:block" onClick={() => modalCon?.trigger(1)} />
                        </div>
                    </Authorized>
                }
            />
            {/* UPPER ROW OF INPUT & BUTTON */}
            <SearchbarAndNavigationButtons
                searchBarPlaceholder="Kunde / Objekt suchen..."
                searchTerm={searchTerm}
                sortDirection={invertSort ?? false}
                setsearchTerm={setsearchTerm}
                sort={() => setinvertSort(!invertSort)}
                showFilter={() => setshowFilter(!showFilter)}
            />
            {isLoading && <Loading />}
            {error && <ErrorPage />}
            {data && !error && !isLoading && (
                <>
                    {/* TABLE */}
                    <BaseGrowBody classProps=" overflow-x-hidden my-4">
                        <BaseTable
                            columns={MajorOrderTableColumns}
                            tbody={displayedMajorOrders?.map((item: MajorOrderGet) => (
                                <MajorOrderTableRow
                                    key={item._id}
                                    id={item._id}
                                    customer={item?.customer_id?.customer}
                                    createdAt={item.createdAt}
                                    object={item?.object_id ?? "Keine Angabe"}
                                    amount={item.amount}
                                    description={item.description}
                                    bills={item.bills ?? []}
                                    archived={item.archived}
                                    state={item.state}
                                />
                            ))}
                        />
                        {(!displayedMajorOrders || displayedMajorOrders.length === 0) && (
                            <p className="text-center text-gray-300 uppercase font-medium pt-4">Keine erstellten Aufträge</p>
                        )}
                    </BaseGrowBody>
                    <BaseFilterModal
                        visible={showFilter}
                        searchdateSince={filterDateSince}
                        searchdateTo={filterDateTo}
                        changeDateTo={setfilterDateTo}
                        changeDateSince={setfilterDateSince}
                        removeFilter={handleRemoveFilter}
                        setVisible={() => setshowFilter(!showFilter)}
                        searchArchived={filterArchived}
                        changeArchived={setfilterArchived}
                        statusType={"major-order"}
                        sessionStorageKey={LocalStorageKeys.MAJOR_ORDER_FILTER}
                    />
                    {/* PAGINATION */}
                    <div className="flex justify-center w-full p-4">
                        <Pagination
                            pageSize={15}
                            defaultCurrent={1}
                            current={page}
                            onChange={handlePageChange}
                            total={data.totalDocs}
                            showSizeChanger={false}
                        />
                    </div>
                </>
            )}
            <AddMajorOrderModal queryKey={queryKey} modalId={1} />
            <FloatingActionButton currentPage="major-order" />
        </BasePage>
    )
}
